import { useGetSitecoreContextId } from '../api/useGetSitecoreContextId';
import { useMemo } from 'react';
import { RESOURCES_CONFIG, validResources } from '../utils';
import {
  isEmbeddedResource,
  USER_SEARCH_RESOURCE_ID,
} from './embeddedResources';

const getDisplayName = ({
  type,
  tenantProductCode,
  name,
}: {
  type: string;
  tenantProductCode: string;
  name?: string;
}): string => {
  if (type === 'personalize' && tenantProductCode === 'cdp') {
    return 'CDP';
  }

  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return parts[1];
  }
  if (parts.length === 1) {
    return parts[0];
  }

  const fallbackLabel =
    RESOURCES_CONFIG[tenantProductCode as keyof typeof RESOURCES_CONFIG]?.label;
  return fallbackLabel || tenantProductCode.toUpperCase();
};

export const useEnhancedResources = (sitecoreContextId: string) => {
  const {
    data,
    isLoading: isContextLoading,
    error,
  } = useGetSitecoreContextId({ sitecoreContextId });

  const updatedResources = useMemo(
    () =>
      data?.resources?.map((resource) => {
        const tenantProductCode = (
          resource.tenantProductCode ||
          resource.type ||
          ''
        ).toLowerCase();

        const isUserCreatedSearch = resource.id === USER_SEARCH_RESOURCE_ID;

        const isEmbeddedResourceHidden =
          !isUserCreatedSearch && isEmbeddedResource(resource);

        const isVisible =
          isUserCreatedSearch ||
          (!isEmbeddedResourceHidden &&
            !!validResources[tenantProductCode as keyof typeof validResources]);

        if (resource.type === 'site') {
          const siteProperty = resource.properties.find(
            ({ id }) => id === 'site',
          )?.value;
          return {
            ...resource,
            tenantDisplayName: siteProperty
              ? `${resource.tenantName} - ${siteProperty}`
              : resource.tenantName,
            name: 'Site Analytics (identifier)',
            isVisible,
          };
        }

        return {
          ...resource,
          tenantDisplayName: resource.tenantName,
          name: getDisplayName({
            type: resource.type,
            tenantProductCode,
            name: resource.name,
          }),
          isVisible,
        };
      }) ?? [],
    [data],
  );

  return useMemo(
    () => ({
      resources: data?.resources ?? [],
      updatedResources,
      isLoading: isContextLoading,
      error,
    }),
    [data, updatedResources, isContextLoading, error],
  );
};
